import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@heroui/spinner';
import { addToast } from '@heroui/toast';
import moment from 'moment';
import Avatar from '@/components/Avatar';
import { useAuth } from '@/contexts/auth-context';
import DeleteConfirmModal from '@/components/DeleteConfirmModal';
import { approveComment, createComment, deleteComment, fetchModerationComments, updateComment } from '@/apiEndpoints';
import { btnGhost, btnPrimary, chip, chipActive, chipIdle, surface } from '@/components/ui/design';

type StatusFilter = 'all' | 'pending' | 'approved' | 'deleted';

const FILTERS: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'deleted', label: 'Deleted' },
];

const STATUS_LABEL: Record<string, string> = {
    pending: 'Pending approval',
    approved: 'Approved',
    deleted: 'Deleted',
};

const STATUS_STYLE: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:ring-amber-500/20',
    approved: 'bg-green-50 text-green-700 ring-green-200 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20',
    deleted: 'bg-red-50 text-red-700 ring-red-200 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20',
};

const actionBtn = 'inline-flex h-9 items-center justify-center rounded-full px-4 text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:focus-visible:ring-nightAccent/70 disabled:opacity-60';
const approveBtn = `${actionBtn} bg-primary text-white hover:bg-primary30 dark:bg-nightAccent dark:text-nightBg dark:hover:bg-primary70`;
const neutralBtn = `${actionBtn} bg-grey99 text-grey10 ring-1 ring-black/[0.06] hover:bg-grey95 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:hover:bg-white/10`;
const dangerBtn = `${actionBtn} text-red-600 ring-1 ring-red-200 hover:bg-red-50 dark:text-red-400 dark:ring-red-500/30 dark:hover:bg-red-500/10`;

const MAX_LENGTH = 1000;
const PAGE_SIZE = 10;

export const pendingCommentsKey = (eventId: string) => ['commentsPendingCount', eventId];

function errorMessage(error: any, fallback: string) {
    const message = error?.response?.data?.message;
    return Array.isArray(message) ? message.join(', ') : message || fallback;
}

export default function CommentsTab({ eventId, commentsDisabled, canEdit, onEditSettings }: { eventId: string; commentsDisabled?: boolean; canEdit?: boolean; onEditSettings?: () => void }) {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    const currentUserId = user?.userId ? String(user.userId) : '';
    const [filter, setFilter] = useState<StatusFilter>('pending');
    const [page, setPage] = useState(1);
    const [replyOpenId, setReplyOpenId] = useState<string | null>(null);
    const [replyText, setReplyText] = useState('');
    const [replyError, setReplyError] = useState('');
    const [editReplyId, setEditReplyId] = useState<string | null>(null);
    const [editReplyText, setEditReplyText] = useState('');
    const [editReplyError, setEditReplyError] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<any | null>(null);

    const listKey = ['moderationComments', eventId, filter, page];

    const { data, isLoading, isError, isFetching, refetch } = useQuery<any>({
        queryKey: listKey,
        queryFn: () => fetchModerationComments(eventId, filter, page, PAGE_SIZE),
        enabled: !!eventId,
        placeholderData: (previous: any) => previous,
        refetchInterval: 30000,
    });

    const comments: any[] = data?.data || [];
    const counts = data?.counts || { pending: 0, approved: 0, deleted: 0 };
    const totalPages = data?.pagination?.totalPages || 1;
    const userInfos: any[] = data?.config?.userInfos || [];
    const findUser = (id?: string) => userInfos.find(u => String(u.id) === String(id));

    const refresh = () => {
        queryClient.invalidateQueries({ queryKey: ['moderationComments', eventId] });
        queryClient.invalidateQueries({ queryKey: pendingCommentsKey(eventId) });
    };

    const approveMutation = useMutation({
        mutationFn: (id: string) => approveComment(id),
        onSuccess: () => {
            addToast({ title: 'Comment approved', color: 'success' });
            refresh();
        },
        onError: (error: any) => {
            addToast({ title: 'Could not approve comment', description: errorMessage(error, 'Please try again.'), color: 'danger' });
            refresh();
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => deleteComment(id),
        onSuccess: () => {
            addToast({ title: 'Comment deleted', color: 'success' });
            setDeleteTarget(null);
            refresh();
        },
        onError: (error: any) => {
            addToast({ title: 'Could not delete comment', description: errorMessage(error, 'Please try again.'), color: 'danger' });
        },
    });

    const replyMutation = useMutation({
        mutationFn: ({ parentId, text }: { parentId: string; text: string }) => createComment(eventId, text, parentId),
        onSuccess: () => {
            setReplyOpenId(null);
            setReplyText('');
            setReplyError('');
            refresh();
        },
        onError: () => {
            setReplyError("Your reply couldn't be sent. Please try again.");
        },
    });

    const editReplyMutation = useMutation({
        mutationFn: ({ id, text }: { id: string; text: string }) => updateComment(id, text),
        onSuccess: () => {
            setEditReplyId(null);
            setEditReplyText('');
            setEditReplyError('');
            addToast({ title: 'Changes saved', color: 'success' });
            refresh();
        },
        onError: (error: any) => {
            setEditReplyError(errorMessage(error, "Your reply couldn't be saved. Please try again."));
        },
    });

    const startEditReply = (r: any) => {
        setEditReplyId(String(r._id));
        setEditReplyText(r.body);
        setEditReplyError('');
    };

    const submitEditReply = (id: string) => {
        const text = editReplyText.trim();
        if (!text) {
            setEditReplyError('Reply cannot be empty.');
            return;
        }
        if (text.length > MAX_LENGTH) {
            setEditReplyError(`Reply cannot exceed ${MAX_LENGTH} characters.`);
            return;
        }
        if (editReplyMutation.isPending) return;
        setEditReplyError('');
        editReplyMutation.mutate({ id, text });
    };

    const renderEditor = (id: string) => (
                <div>
                    <label htmlFor={`edit-reply-${id}`} className="sr-only">Edit reply</label>
                    <textarea
                        id={`edit-reply-${id}`}
                        value={editReplyText}
                        onChange={e => { setEditReplyText(e.target.value); setEditReplyError(''); }}
                        maxLength={MAX_LENGTH}
                        rows={3}
                        disabled={editReplyMutation.isPending}
                        className="w-full resize-y rounded-2xl bg-white px-3 py-2 text-[14px] text-grey10 ring-1 ring-black/[0.06] focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:focus:ring-nightAccent/60"
                    />
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-xs text-grey40 dark:text-nightMuted">{editReplyText.length}/{MAX_LENGTH}</span>
                        <div className="flex gap-2">
                            <button type="button" className={neutralBtn} disabled={editReplyMutation.isPending} onClick={() => setEditReplyId(null)}>Cancel</button>
                            <button type="button" className={approveBtn} disabled={editReplyMutation.isPending || !editReplyText.trim()} onClick={() => submitEditReply(id)}>
                                {editReplyMutation.isPending ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                    {editReplyError && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{editReplyError}</p>}
                </div>
    );

    const openReply = (id: string) => {
        setReplyOpenId(replyOpenId === id ? null : id);
        setReplyText('');
        setReplyError('');
    };

    const submitReply = (parentId: string) => {
        const text = replyText.trim();
        if (!text) {
            setReplyError('Reply cannot be empty.');
            return;
        }
        if (text.length > MAX_LENGTH) {
            setReplyError(`Reply cannot exceed ${MAX_LENGTH} characters.`);
            return;
        }
        if (replyMutation.isPending) return;
        setReplyError('');
        replyMutation.mutate({ parentId, text });
    };

    const changeFilter = (next: StatusFilter) => {
        setFilter(next);
        setPage(1);
    };

    return (
        <div className="w-full pb-8">
            <div className="mb-5 flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white">Comments</h2>
                {commentsDisabled && (
                    <span className="inline-flex items-center rounded-full bg-grey95 px-3 py-1 text-xs font-semibold text-grey40 dark:bg-white/10 dark:text-nightMuted">Disabled</span>
                )}
                <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${counts.pending > 0 ? 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400' : 'bg-grey99 text-grey40 dark:bg-white/[0.06] dark:text-nightMuted'}`}
                    aria-label={`${counts.pending} pending comments`}
                >
                    {counts.pending} pending
                </span>
                {isFetching && !isLoading && <Spinner size="sm" aria-label="Refreshing comments" />}
            </div>

            {commentsDisabled && (
                <div className={`${surface} mb-4 px-5 py-4 text-sm text-grey30 dark:text-nightTextSecondary`} role="status">
                    <div className="mb-1 text-base font-bold text-grey10 dark:text-white">Comments are disabled</div>
                    {canEdit && onEditSettings ? (
                        <>
                            Comments are currently disabled for this event. To allow attendees to comment,{' '}
                            <button
                                type="button"
                                onClick={onEditSettings}
                                className="font-semibold text-primary underline underline-offset-2 hover:text-primary30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:text-nightAccent dark:hover:text-primary70"
                            >
                                edit your event settings
                            </button>{' '}
                            and enable comments. Existing comments are kept and stay hidden until then.
                        </>
                    ) : (
                        <>Comments are currently disabled for this event. Ask an event admin with edit access to enable comments in the event settings. Existing comments are kept and stay hidden until then.</>
                    )}
                </div>
            )}

            {counts.pending > 0 && filter !== 'pending' && (
                <button
                    type="button"
                    onClick={() => changeFilter('pending')}
                    className="mb-4 flex w-full items-center justify-between rounded-2xl bg-amber-50 px-5 py-3 text-left text-sm font-semibold text-amber-800 ring-1 ring-amber-200 transition-colors hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20"
                >
                    <span>{counts.pending === 1 ? '1 comment needs your review' : `${counts.pending} comments need your review`}</span>
                    <span aria-hidden="true">Review</span>
                </button>
            )}

            <div className="mb-5 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Filter comments by status">
                {FILTERS.map(f => {
                    const count = f.key === 'all' ? null : counts[f.key];
                    return (
                        <button
                            key={f.key}
                            type="button"
                            role="tab"
                            aria-selected={filter === f.key}
                            onClick={() => changeFilter(f.key)}
                            className={`${chip} ${filter === f.key ? chipActive : chipIdle} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60`}
                        >
                            {f.label}{count !== null ? ` (${count})` : ''}
                        </button>
                    );
                })}
            </div>

            {isLoading ? (
                <div className="flex justify-center py-16">
                    <Spinner aria-label="Loading comments" />
                </div>
            ) : isError && comments.length === 0 ? (
                <div className={`${surface} flex flex-col items-center gap-3 px-6 py-12 text-center`} role="alert">
                    <p className="text-sm text-grey30 dark:text-nightTextSecondary">Comments could not be loaded.</p>
                    <button type="button" className={btnGhost} onClick={() => refetch()}>Try again</button>
                </div>
            ) : comments.length === 0 ? (
                <div className={`${surface} px-6 py-14 text-center text-sm text-grey40 dark:text-nightMuted`}>
                    {filter === 'all' ? 'No comments yet.' : `No ${STATUS_LABEL[filter].toLowerCase()} comments.`}
                </div>
            ) : (
                <>
                    {isError && (
                        <div className="mb-3 rounded-2xl bg-red-50 px-5 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400" role="alert">
                            Could not refresh comments. Showing the last loaded list.
                        </div>
                    )}
                    <ul className="space-y-3">
                        {comments.map(c => {
                            const id = String(c._id);
                            const status: string = c.status || 'pending';
                            const author = c.userInfo?.firstName ? `${c.userInfo.firstName} ${c.userInfo.lastName || ''}`.trim() : 'Unknown user';
                            const deleter = findUser(c.deletedBy);
                            const isDeleted = status === 'deleted';
                            const busy = (approveMutation.isPending && approveMutation.variables === id) || (deleteMutation.isPending && deleteMutation.variables === id);
                            return (
                                <li
                                    key={id}
                                    className={`rounded-[24px] bg-white p-5 ring-1 dark:bg-white/[0.04] ${status === 'pending' ? 'ring-amber-300 dark:ring-amber-500/40' : 'ring-black/[0.06] dark:ring-white/10'}`}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <Avatar
                                                src={c.userInfo?.profileImage}
                                                name={c.userInfo?.firstName}
                                                seed={c.userInfo?.userId || c.userInfo?.id || c.userInfo?.email}
                                                size={40}
                                                className="ring-1 ring-black/[0.06] dark:ring-white/10"
                                            />
                                            <div className="min-w-0">
                                                <div className="truncate text-[15px] font-bold text-grey10 dark:text-white">{author}</div>
                                                <time className="text-xs font-semibold text-grey40 dark:text-nightMuted" dateTime={c.createdAt}>
                                                    {moment(c.createdAt).format('MMM D, YYYY [at] h:mm A')}
                                                </time>
                                            </div>
                                        </div>
                                        <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${STATUS_STYLE[status]}`}>
                                            {STATUS_LABEL[status]}
                                        </span>
                                    </div>

                                    {editReplyId === id ? (
                                        <div className="mt-3">{renderEditor(id)}</div>
                                    ) : (
                                    <p className={`mt-3 whitespace-pre-wrap break-words text-[15px] leading-relaxed ${isDeleted ? 'text-grey40 line-through dark:text-nightMuted' : 'text-grey30 dark:text-nightTextSecondary'}`}>
                                        {c.body}
                                    </p>
                                    )}

                                    {isDeleted && (
                                        <p className="mt-2 text-xs text-grey40 dark:text-nightMuted">
                                            Deleted {c.deletedAt ? moment(c.deletedAt).format('MMM D, YYYY [at] h:mm A') : ''}
                                            {c.deletedBy ? (String(c.deletedBy) === String(c.userId) ? ' by the author' : deleter?.firstName ? ` by ${deleter.firstName}` : ' by an admin') : ''}
                                        </p>
                                    )}

                                    {c.replies?.length > 0 && !isDeleted && (
                                        <div className="ml-3 mt-4 space-y-2 border-l-2 border-primary/30 pl-4 dark:border-nightAccent/40">
                                            {c.replies.map((r: any) => (
                                                <div key={String(r._id)} className="rounded-[18px] bg-grey99 p-3 dark:bg-white/[0.04]">
                                                    <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                                                        <span className="font-bold text-grey10 dark:text-white">{r.userInfo?.firstName || 'Admin'}</span>
                                                        <span className="rounded-full bg-primary/10 px-2 py-0.5 font-semibold text-primary dark:bg-nightAccent/15 dark:text-nightAccent">Admin reply</span>
                                                        <time className="text-grey40 dark:text-nightMuted" dateTime={r.createdAt}>{moment(r.createdAt).fromNow()}</time>
                                                    </div>
                                                    {editReplyId === String(r._id) ? (
                                                        renderEditor(String(r._id))
                                                    ) : (
                                                        <>
                                                            <p className="whitespace-pre-wrap break-words text-[14px] text-grey30 dark:text-nightTextSecondary">{r.body}</p>
                                                            {currentUserId && String(r.userId) === currentUserId && !commentsDisabled && (
                                                                <button type="button" className={`${neutralBtn} mt-2 !h-8`} onClick={() => startEditReply(r)}>Edit</button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {!isDeleted && (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            {status === 'pending' && (
                                                <button type="button" className={approveBtn} disabled={busy} onClick={() => approveMutation.mutate(id)}>
                                                    {approveMutation.isPending && approveMutation.variables === id ? 'Approving...' : 'Approve'}
                                                </button>
                                            )}
                                            {!commentsDisabled && (
                                                <button type="button" className={neutralBtn} aria-expanded={replyOpenId === id} onClick={() => openReply(id)}>
                                                    Reply
                                                </button>
                                            )}
                                            {!commentsDisabled && (
                                                <button type="button" className={neutralBtn} disabled={busy} onClick={() => startEditReply(c)}>
                                                    Edit
                                                </button>
                                            )}
                                            <button type="button" className={dangerBtn} disabled={busy} onClick={() => setDeleteTarget(c)}>
                                                Delete
                                            </button>
                                        </div>
                                    )}

                                    {replyOpenId === id && (
                                        <div className="mt-3">
                                            <label htmlFor={`reply-${id}`} className="sr-only">Reply to {author}</label>
                                            <textarea
                                                id={`reply-${id}`}
                                                value={replyText}
                                                onChange={e => { setReplyText(e.target.value); setReplyError(''); }}
                                                maxLength={MAX_LENGTH}
                                                rows={3}
                                                placeholder="Write a reply..."
                                                disabled={replyMutation.isPending}
                                                className="w-full resize-y rounded-2xl bg-grey99 px-4 py-3 text-[15px] text-grey10 ring-1 ring-black/[0.04] placeholder:text-grey60 focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10 dark:placeholder:text-nightMuted dark:focus:ring-nightAccent/60"
                                            />
                                            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                                                <span className="text-xs text-grey40 dark:text-nightMuted">{replyText.length}/{MAX_LENGTH}</span>
                                                <div className="flex gap-2">
                                                    <button type="button" className={neutralBtn} onClick={() => openReply(id)} disabled={replyMutation.isPending}>Cancel</button>
                                                    <button
                                                        type="button"
                                                        className={approveBtn}
                                                        disabled={replyMutation.isPending || !replyText.trim()}
                                                        onClick={() => submitReply(id)}
                                                    >
                                                        {replyMutation.isPending ? 'Sending...' : 'Send reply'}
                                                    </button>
                                                </div>
                                            </div>
                                            {replyError && <p className="mt-2 text-sm text-red-600 dark:text-red-400" role="alert">{replyError}</p>}
                                            {status === 'pending' && (
                                                <p className="mt-2 text-xs text-grey40 dark:text-nightMuted">This reply stays hidden until the comment is approved.</p>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>

                    {totalPages > 1 && (
                        <div className="mt-6 flex items-center justify-center gap-3">
                            <button type="button" className={btnGhost} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</button>
                            <span className="text-sm text-grey40 dark:text-nightMuted">Page {page} of {totalPages}</span>
                            <button type="button" className={btnPrimary} disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
                        </div>
                    )}
                </>
            )}

            <DeleteConfirmModal
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(String(deleteTarget._id))}
                title="Delete comment"
                message="Delete this comment? It will no longer be visible publicly."
                loading={deleteMutation.isPending}
            />
        </div>
    );
}
